import {
  Category,
  Intent,
  RevenueChangeGroup,
  WhiteSpaceGroup,
  WhiteSpaceStatus,
} from 'enums';
import { css } from 'styled-components';

import makeProfitabilityTheme from './makeProfitabilityTheme';
import { makeWhiteSpaceLegendTheme } from './utils';

const colors = {
  amber: '#FFBF00',
  yellow: '#ffcc00',
  lightBlueGrey: '#c0cbe3',
  lightGrey: '#bbb',
  lightGrey_sg: '#f4f5f6',
  grey: '#eee',
  lightestGrey: '#e5e5e5',
  lighterGrey: '#f6f6f6',
  lightPeriwinkle: '#d1ddeb',
  skyBlue: '#4a90e2',
  blue: '#4479ff',
  blueHighlight: '#89a3ff',
  blueDark: '#386CEF',
  blueLight: '#e9efff',
  navyBlue: '#012060',
  navyBlueLight: `#334c7f`,
  darkBlue: 'rgb(24, 39, 57)',
  darkBlueLight: 'rgba(24, 39, 57, 0.75)',
  darkerBlue: '#090f16',
  midnightBlue: '#172739',
  dusk: '#42526e',
  orange: 'rgba(236,97,0,1)',
  orangeHighlight: 'rgba(236,148,71,1)',
  brightAqua: '#00ebc2',
  softGreen: '#3ad88f',
  limeGreen: '#3ad88f',
  darkGreen: '#5aa25f',
  changeGreen: 'rgb(171, 227, 156)',
  veryLightBlue: '#e5e9f2',
  white: '#ffffff',
  black: '#000000',
  changeRose: 'rgb(243, 184, 180)',
  rose: '#df5555',
  deepRose: '#b95461',
  deepRoseDark: '#ad4754',
  brickRed: '#cd3a4c',
  brickDark: '#a92b3a',
  steel: '#6e7a93',
  snowWhite: '#fafafa',
  turquoise: '#28ccf0',
  turquoiseLight: 'rgba(40, 204, 240, 0.5)',
  paleGrey: '#f5f6fa',
  offWhite: '#f9fafb',

  platinum: '#e5e4e2',
  gold: '#ffcc00',
  silver: '#c0c0c0',
  bronze: '#cd7f32',

  /* react-select colors */
  primary: '#2684FF',
  primary75: '#4C9AFF',
  primary50: '#B2D4FF',
  primary25: '#DEEBFF',
  danger: '#DE350B',
  dangerLight: '#FFBDAD',
  neutral0: 'hsl(0, 0%, 100%)',
  neutral5: 'hsl(0, 0%, 95%)',
  neutral10: 'hsl(0, 0%, 90%)',
  neutral20: 'hsl(0, 0%, 80%)',
  neutral30: 'hsl(0, 0%, 70%)',
  neutral40: 'hsl(0, 0%, 60%)',
  neutral50: 'hsl(0, 0%, 50%)',
  neutral60: 'hsl(0, 0%, 40%)',
  neutral70: 'hsl(0, 0%, 30%)',
  neutral80: 'hsl(0, 0%, 20%)',
  neutral90: 'hsl(0, 0%, 10%)',
  /* end react-select colors */
};

const whiteSpacePalette = {
  [WhiteSpaceGroup.AGREEMENT_TYPE]: {
    groupHeader: 'rgba(174, 157, 240, 0.4)',
    columnHeader: 'rgba(174, 157, 240, 0.5)',
    cell: '#fff', // HSLUV_6
    [WhiteSpaceStatus.ACTIVE]: 'rgba(81, 178, 233, 0.8)', // HSV_6
    [WhiteSpaceStatus.ANNOTATED]: 'rgba(210, 210, 210)',
    [WhiteSpaceStatus.PROSPECT]: 'rgb(68, 175, 131)', // HSV_4
    [WhiteSpaceStatus.OPEN_OPPORTUNITY]: '#f4cccc',
  },
  [WhiteSpaceGroup.PRODUCT_SUBCATEGORY]: {
    groupHeader: 'rgba(201, 164, 70, 0.4)',
    columnHeader: 'rgba(201, 164, 70, 0.5)',
    cell: '#fff',
    [WhiteSpaceStatus.ACTIVE]: 'rgba(81, 178, 233, 0.65)', // HSV_6
    [WhiteSpaceStatus.ANNOTATED]: 'rgba(210, 210, 210)',
    [WhiteSpaceStatus.PROSPECT]: 'rgb(109, 204, 92)', // HSV_4
    [WhiteSpaceStatus.OPEN_OPPORTUNITY]: '#f4cccc',
  },

  [WhiteSpaceGroup.INVOICED_PRODUCT]: {
    groupHeader: 'rgb(202, 184, 136)',
    columnHeader: 'rgb(202, 184, 136)',
    cell: '#fff',
    [WhiteSpaceStatus.ACTIVE]: 'rgba(81, 178, 233, 0.5)', // HSV_6
    [WhiteSpaceStatus.ANNOTATED]: 'rgba(210, 210, 210)',
    [WhiteSpaceStatus.PROSPECT]: 'rgb(109, 204, 92)', // HSV_4
    [WhiteSpaceStatus.OPEN_OPPORTUNITY]: '#f4cccc',
  },
};

export type WhiteSpacePalette = typeof whiteSpacePalette;

const whitespace = {
  palette: whiteSpacePalette,
  legend: makeWhiteSpaceLegendTheme(whiteSpacePalette),
};

export const profitability = makeProfitabilityTheme();

const profitTrack = {
  [RevenueChangeGroup.SAME]: {
    legend: {
      background: '#ccc',
      width: '160px',
      $iconColor: '#fff',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [RevenueChangeGroup.EXISTING]: {
    legend: {
      background: '#ccc',
      width: '160px',
      $iconColor: '#fff',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [RevenueChangeGroup.CHURNED]: {
    legend: {
      background: 'rgb(237, 126, 115)',
      width: '160px',
      $iconColor: '#fff',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [RevenueChangeGroup.DECREASED]: {
    legend: {
      background: 'rgb(243, 184, 180)',
      width: '160px',
      $iconColor: '#fff',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [RevenueChangeGroup.INCREASED]: {
    legend: {
      background: 'rgb(171, 227, 156)',
      width: '160px',
      $iconColor: '#fff',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [RevenueChangeGroup.NEW]: {
    legend: {
      background: 'rgb(103, 175, 77)',
      width: '160px',
      $iconColor: '#fff',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [RevenueChangeGroup.NET]: {
    color: {
      primary: 'rgb(39, 97, 149)',
      secondary: 'rgb(143, 188, 245)',
    },
    legend: {
      color: '#fff',
      background: 'rgb(39, 97, 149)',
      width: '160px',
      $iconColor: '#fff',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [RevenueChangeGroup.TOTAL]: {
    color: {
      primary: 'rgb(39, 97, 149)',
      secondary: 'rgb(143, 188, 245)',
    },
    legend: {
      color: '#fff',
      background: 'rgb(39, 97, 149)',
      width: '160px',
      $iconColor: '#fff',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
};

const category = {
  [Category.PLATINUM]: {
    colors: {
      primary: colors.platinum,
      secondary: 'rgba(229,228,226,0.80)',
    },
    legend: {
      background: colors.platinum,
      width: '68px',
      $iconColor: '#fff',
      $iconDisplay: 'none',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [Category.GOLD]: {
    colors: {
      primary: colors.gold,
      secondary: 'rgba(255,204,0,0.80)',
    },
    legend: {
      background: colors.gold,
      width: '68px',
      $iconColor: '#fff',
      $iconDisplay: 'none',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [Category.SILVER]: {
    colors: {
      primary: colors.silver,
      secondary: 'rgba(192,192,192,0.80)',
    },
    legend: {
      background: colors.silver,
      width: '68px',
      $iconColor: '#fff',
      $iconDisplay: 'none',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
  [Category.BRONZE]: {
    colors: {
      primary: colors.bronze,
      secondary: 'rgba(205,127,50,0.80)',
    },
    legend: {
      background: colors.bronze,
      width: '68px',
      $iconColor: '#fff',
      $iconDisplay: 'none',
      $iconHoverColor: '#fff',
      $iconHoverOpacity: 0.6,
    },
  },
};

const intent: Record<
  Intent,
  {
    background: string;
    backgroundSoft: string;
    foreground: string;
    hover: string;
  }
> = {
  none: {
    background: 'transparent',
    backgroundSoft: 'transparent',
    foreground: colors.lightPeriwinkle,
    hover: colors.blue,
  },
  primary: {
    background: colors.blue,
    backgroundSoft: 'rgba(68,121,255,0.3)',
    foreground: colors.white,
    hover: colors.blueDark,
  },
  tertiary: {
    background: 'transparent',
    backgroundSoft: 'transparent',
    foreground: colors.lightPeriwinkle,
    hover: colors.blue,
  },
  quaternary: {
    background: colors.darkBlue,
    backgroundSoft: colors.darkBlueLight,
    foreground: colors.white,
    hover: colors.darkBlueLight,
  },
  quinary: {
    background: colors.navyBlue,
    backgroundSoft: colors.navyBlueLight,
    foreground: colors.white,
    hover: colors.navyBlueLight,
  },
  secondary: {
    background: colors.white,
    backgroundSoft: colors.white,
    foreground: colors.darkBlue,
    hover: colors.snowWhite,
  },
  success: {
    background: colors.brightAqua,
    backgroundSoft: 'rgba(0, 236, 194, 0.3)',
    foreground: colors.darkBlue,
    hover: colors.darkGreen,
  },
  warning: {
    background: colors.amber,
    backgroundSoft: 'rgba(255,191,0,0.3)',
    foreground: colors.black,
    hover: colors.yellow,
  },
  danger: {
    background: colors.deepRose,
    backgroundSoft: 'rgba(185,84,97,0.3)',
    foreground: colors.white,
    hover: colors.deepRoseDark,
  },
};

const styles = {
  boxShadow: {
    elevation1: `0 0 0 1px ${colors.lightPeriwinkle}`,
  },
  borderRadius: '3px',
};

const text = {
  PageTitle: css`
    font-family: Inter;
    font-size: 24px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: -0.3px;
    color: ${colors.darkBlue};
  `,
  SectionTitle: css`
    font-family: Inter;
    font-size: 16px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: -0.3px;
    color: ${colors.blue};
  `,
  CardTitle: css`
    font-family: Inter;
    font-size: 15px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: -0.3px;
    color: ${colors.darkBlue};
  `,
  MainNavActive: css`
    font-family: Inter;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    color: ${colors.darkBlue};
  `,
  MainNavItem: css`
    font-family: Inter;
    font-size: 14px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    color: ${colors.steel};
  `,
  ArticleLink: css`
    font-family: Inter;
    font-size: 13px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    color: ${colors.blue};
  `,
  NavActive: css`
    font-family: Inter;
    font-size: 13px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    color: ${colors.darkBlue};
  `,
  List: css`
    font-family: Inter;
    font-size: 13px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    color: ${colors.steel};
  `,
  TableHead: css`
    font-family: Inter;
    font-size: 13px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    line-height: normal;
    letter-spacing: normal;
    color: ${colors.steel};
  `,
  BodyBold: css`
    font-family: Inter;
    font-size: 13px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    color: ${colors.darkBlue};
  `,
  body: css`
    font-family: Inter;
    font-size: 14px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    color: ${colors.darkBlue};
  `,
  BodySecondary: css`
    font-family: Inter;
    font-size: 13px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    color: ${colors.steel};
  `,
  BodySmall: css`
    font-family: Inter;
    font-size: 12px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    color: ${colors.steel};
  `,
};

const transitions = {
  backgroundColor: 'background-color 100ms ease-in',
};

const layout = {
  leftNav: {
    leftPadding: '1.5rem',
    width: '0px',
  },
  topHeader: {
    height: '0px',
  },
};

const theme = {
  // react-select attr
  borderRadius: 4,
  category,
  colors,
  intent,
  layout,
  profitability,
  profitTrack,
  text,
  styles,
  space: [0, 10, 20, 30, 40],
  // react-select attr
  spacing: {
    baseUnit: 4,
    controlHeight: 38,
    menuGutter: 8,
  },
  transitions,
  breakpoints: ['32em', '48em', '64em', '86em'],
  whitespace,
};

export default theme;
